import { FC, memo } from "react";
import { Formik, Form } from "formik";
import { IconButton, Stack, styled } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import CardActions from "@mui/material/CardActions";
import Stepper from "ui/organisms/Stepper";
import { schemaArray } from "../schemas/schemas";
import { getInitialValues } from "../constants/initialValues";
import { Answers, SurveyPerson, SurveyToken } from "types";
import getCurrentUser from "lib/auth/pca";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Wizard from "./Wizard";
import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";
import Step3 from "./steps/Step3";
import Step4 from "./steps/Step4";
import Step5 from "./steps/Step5";
import Step6 from "./steps/Step6";
import Step7 from "./steps/Step7";
import Step8 from "./steps/Step8";
import Step9 from "./steps/Step9";
import Step10 from "./steps/Step10";
import Step11 from "./steps/Step11";
import Step12 from "./steps/Step12";
import Step13 from "./steps/Step13";
import Step14 from "./steps/Step14";
import Step15 from "./steps/Step15";
import Step16 from "./steps/Step16";
import Step17 from "./steps/Step17";
import Step18 from "./steps/Step18";
import Step19 from "./steps/Step19";
import Step20 from "./steps/Step20";
import { Link } from "react-router-dom";
import { YES } from "../constants/uiControls";
import { useSection } from "lib/context/section";

type Props = {
  loading: boolean;
  answers: Answers[];
  tokenInfo: SurveyToken;
  handleSubmit: Function;
  isTokenExpired: boolean;
  answerComplement: Answers;
  surveyPerson: SurveyPerson | null;
  setIdentificationNumber: (identificationNumber: string) => void;
};

const SurveyForm: FC<Props> = memo(
  ({
    loading,
    answers,
    tokenInfo,
    surveyPerson,
    handleSubmit,
    isTokenExpired,
    answerComplement,
    setIdentificationNumber,
  }) => {
    const { sectionId, setSectionId } = useSection();
    const { lastSectionId } = tokenInfo ?? 0;

    const handleNext = () => {
      setSectionId(sectionId + 1);
    };

    const handleBack = () => {
      setSectionId(sectionId - 1);
    };

    const disabled = !isEmpty(surveyPerson);
    return (
      <Formik
        initialValues={getInitialValues(sectionId, answers, surveyPerson)}
        enableReinitialize={true}
        validateOnChange={true}
        validationSchema={schemaArray[sectionId]}
        onSubmit={(values: any) => {
          const currentUser = getCurrentUser() ?? surveyPerson?.email;
          let valuesMapped: any[] = [];
          let templateMapped: any[] = [];
          if (values?.template) {
            if (values?.hasChildren?.value === YES || values?.numberOfPersons) {
              if (values?.childrenRegistered) {
                values.childrenRegistered.value = values?.template?.length || 0;
              }
              if (values?.numberOfPersons) {
                values.numberOfPersons.value = values?.template?.length || 0;
              }
              templateMapped = values.template.map((template: any, index: number) => {
                return Object.entries(template).flatMap(([key, value]: any) => {
                  if (Array.isArray(value)) {
                    return value.map((item: any) => ({
                      value: item,
                      questionID: key,
                      subSectionId: index + 1,
                      updatedBy: currentUser,
                      personID: tokenInfo.personId,
                      surveyTokenID: tokenInfo.surveyTokenId,
                    }));
                  } else
                    return {
                      questionID: key,
                      value: value,
                      subSectionId: index + 1,
                      updatedBy: currentUser,
                      personID: tokenInfo.personId,
                      surveyTokenID: tokenInfo.surveyTokenId,
                    };
                });
              });
              values.template = [];
              valuesMapped = Object.values(values)
                .filter((item: any) => {
                  if (!Array.isArray(item)) return item;
                })
                .map((value: any) => ({ ...value, ...answerComplement }))
                .flat();
            } else {
              delete values.template;
              delete values.numberOfPersons;
              values.childrenRegistered = { ...values?.childrenRegistered, value: 0 };
              valuesMapped = Object.values(values)
                .filter((item: any) => {
                  if (!Array.isArray(item)) return item;
                })
                .map((value: any) => ({ ...value, ...answerComplement, updatedBy: currentUser }))
                .flat();
            }
          } else {
            valuesMapped = Object.values(values)
              .filter((item: any) => {
                if (Array.isArray(item) && item?.length) return item;
                if (typeof item === "object") return item;
              })
              .map((value: any) => {
                if (Array.isArray(value)) {
                  return value?.map((item: any) => ({ ...item, ...answerComplement, updatedBy: currentUser })).flat();
                } else {
                  return { ...value, ...answerComplement, updatedBy: currentUser };
                }
              })
              .flat();
          }

          const valuesFinal = [...valuesMapped, ...templateMapped].flat();
          handleSubmit(valuesFinal).then((response: any) => {
            if (response?.error) return;
            handleNext();
          });
        }}
      >
        {(formikProps) => (
          <Form>
            <Wizard>
              <div></div>
              <Step1
                disabled={disabled}
                formik={formikProps}
                lastSectionId={lastSectionId}
                setIdentificationNumber={setIdentificationNumber}
              />
              <Step2 formik={formikProps} sectionId={sectionId} />
              <Step3 formik={formikProps} sectionId={sectionId} />
              <Step4 formik={formikProps} sectionId={sectionId} />
              <Step5 formik={formikProps} sectionId={sectionId} />
              <Step6 formik={formikProps} sectionId={sectionId} />
              <Step7 formik={formikProps} sectionId={sectionId} />
              <Step8 formik={formikProps} sectionId={sectionId} />
              <Step9 formik={formikProps} sectionId={sectionId} />
              <Step10 formik={formikProps} sectionId={sectionId} />
              <Step11 formik={formikProps} sectionId={sectionId} />
              <Step12 formik={formikProps} sectionId={sectionId} />
              <Step13 formik={formikProps} sectionId={sectionId} />
              <Step14 formik={formikProps} sectionId={sectionId} />
              <Step15 formik={formikProps} sectionId={sectionId} />
              <Step16 formik={formikProps} sectionId={sectionId} />
              <Step17 formik={formikProps} sectionId={sectionId} />
              <Step18 formik={formikProps} sectionId={sectionId} />
              <Step19 formik={formikProps} sectionId={sectionId} />
              <Step20 formik={formikProps} sectionId={sectionId} />
              <Stack spacing={1} alignItems='center'>
                <Link to='/'>
                  <IconButton color='inherit' aria-label='open drawer' edge='start'>
                    <CheckCircleOutlineIcon fontSize='large' color='success' />
                  </IconButton>
                </Link>
              </Stack>
            </Wizard>

            {sectionId <= 20 && (
              <CardActionsStyled>
                <Stepper
                  stepsNumber={20}
                  loading={loading}
                  disabled={disabled}
                  handleBack={handleBack}
                  isTokenExpired={isTokenExpired}
                />
              </CardActionsStyled>
            )}
          </Form>
        )}
      </Formik>
    );
  }
);

export default SurveyForm;

const CardActionsStyled = styled(CardActions)`
  //padding: 16px 0;
`;
